import { Head } from '@inertiajs/react'
import { useCallback, useMemo, useState } from 'react'

import LinedTextArea from '../../components/tools/LinedTextArea'
import ToolWorkspace from '../../components/tools/ToolWorkspace'
import {
  noopToolSwap,
  type ToolAbout,
  ToolIoChip,
  WrapLinesPanelSwitch,
} from '../../components/tools/toolChrome'
import { getCodec, JWT_TOOL_CODECS, MAX_CODEC_INPUT_CHARS } from '../../lib/codecs'

const JWT_CODEC_ID = JWT_TOOL_CODECS[0].id

const JWT_ABOUT: ToolAbout = {
  description:
    'Inspect JWTs locally. The tool splits the compact form into header and payload, decodes each Base64URL part, and prints JSON with indentation. It does not verify signatures or validate claims. Treat the output as display only, not proof that a token is valid.',
  libraries: [
    { name: 'MDN: atob', href: 'https://developer.mozilla.org/en-US/docs/Web/API/atob' },
    { name: 'MDN: TextDecoder', href: 'https://developer.mozilla.org/en-US/docs/Web/API/TextDecoder' },
    { name: 'MDN: JSON.parse and JSON.stringify', href: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON' },
  ],
}

export default function JwtTool() {
  const codec = useMemo(() => getCodec(JWT_CODEC_ID)!, [])
  const [mode, setMode] = useState<'encode' | 'decode'>('encode')
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lineWrap, setLineWrap] = useState(false)

  const run = useCallback(async () => {
    try {
      setError(null)
      if (input.length > MAX_CODEC_INPUT_CHARS) {
        setError(`Input exceeds ${MAX_CODEC_INPUT_CHARS.toLocaleString()} characters (browser safety limit).`)
        return
      }
      setBusy(true)
      const out = await Promise.resolve(codec.encode(input))
      setOutput(out)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Decode failed')
    } finally {
      setBusy(false)
    }
  }, [input, codec])

  const copy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
    } catch {
      setError('Could not copy to clipboard')
    }
  }, [output])

  const inputColumn = (
    <LinedTextArea
      fillHeight
      value={input}
      onChange={(e) => setInput(e.target.value.slice(0, MAX_CODEC_INPUT_CHARS))}
      maxLength={MAX_CODEC_INPUT_CHARS}
      rows={14}
      wrapLines={lineWrap}
      placeholder={codec.placeholderEncode}
      aria-label="JWT input"
    />
  )

  const toolSettings = <WrapLinesPanelSwitch lineWrap={lineWrap} onLineWrapChange={setLineWrap} />

  return (
    <>
      <Head title="JWT decoder" />
      <ToolWorkspace
        heading="JWT decode (no verify)"
        inputHeaderStart={<ToolIoChip>INPUT (RAW)</ToolIoChip>}
        toolSettings={toolSettings}
        inputHeaderEnd={
          <span className="font-mono text-[10px] text-on-surface-variant uppercase">{codec.hintEncode}</span>
        }
        inputColumn={inputColumn}
        mode={mode}
        setMode={setMode}
        encodeOnly
        swapDisabled
        swapTitle="Not available"
        onSwap={noopToolSwap}
        onExecute={run}
        executeBusy={busy}
        output={output}
        onCopy={copy}
        error={error}
        statsCharValue={String(input.length).padStart(6, '0')}
        statsAlgorithm="JWT"
        statsOutputLen={String(output.length).padStart(6, '0')}
        statsModeLabel="DECODE"
        outputLineNumbers
        lineWrap={lineWrap}
        about={JWT_ABOUT}
      />
    </>
  )
}
